import UseOrEquipModal from 'components/Possessions/components/UseOrEquipModal';
import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { equipQuality } from 'actions/myself';
import { useQuality as _useQuality } from 'actions/storylet';
import getIsEquipped from 'selectors/possessions/getIsEquipped';
import { IAppState } from 'types/app';
import AvailableItemComponent from './AvailableItemComponent';

interface OwnProps {
  currentlyInStorylet: boolean,
  description: string,
  id: number,
  image: string,
  level: number,
  name: string,
  useEventId?: number,
}

type StateProps = ReturnType<typeof mapStateToProps> & {
  dispatch: Function,
};

function AvailableItem(props: OwnProps & StateProps & RouteComponentProps) {
  const {
    currentlyInStorylet,
    description,
    dispatch,
    history,
    id,
    image,
    isChanging,
    isEquipped,
    itemsUsableHere,
    level,
    name,
    useEventId,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const canPlayerUseItems = useMemo(() => {
    return itemsUsableHere && !currentlyInStorylet;
  }, [
    itemsUsableHere,
    currentlyInStorylet,
  ]);

  const handleEquip = useCallback(() => {
    setIsModalOpen(false);
    if (isChanging) {
      return;
    }
    dispatch(equipQuality(id));
  }, [
    dispatch,
    id,
    isChanging,
  ]);

  const handleUse = useCallback(() => {
    setIsModalOpen(false);
    return dispatch(_useQuality(id, history));
  }, [
    dispatch,
    history,
    id,
  ]);

  const handleClick = useCallback(() => {
    if (useEventId === undefined) {
      return handleEquip();
    }
    if (!canPlayerUseItems) {
      console.info('no item use; automatically equipping');
      return handleEquip();
    }
    setIsModalOpen(true);
  }, [
    canPlayerUseItems,
    handleEquip,
    useEventId,
  ]);

  const smallButtons = useMemo(() => {
    const smallButtons = [{
      label: 'Equip',
      action: handleEquip,
    }];
    // Sometimes they're also usable
    if (!!useEventId && !currentlyInStorylet) {
      const useButton = {
        label: 'Use',
        action: handleUse,
      };
      smallButtons.push(useButton);
    }
    return smallButtons;
  }, [
    currentlyInStorylet,
    handleEquip,
    handleUse,
    useEventId,
  ]);

  // If this is an equipped AvailableItem and we only have 1, then we shouldn't show it
  if (isEquipped && level <= 1) {
    return null;
  }

  // Build the tooltip data, including the buttons
  const tooltipData = {
    description,
    id,
    image,
    level,
    name,
    useEventId,
    smallButtons,
  };

  return (
    <Fragment>
      <AvailableItemComponent
        hasUseEventId={canPlayerUseItems && !!useEventId}
        id={id}
        image={image}
        level={level - (isEquipped ? 1 : 0)}
        name={name}
        onClick={handleClick}
        tooltipData={tooltipData}
        tooltipTimeout={useEventId && 500}
      />
      <UseOrEquipModal
        currentlyInStorylet={currentlyInStorylet}
        isOpen={isModalOpen}
        onEquip={handleEquip}
        onRequestClose={() => setIsModalOpen(false)}
        onUse={handleUse}
        quality={{ image, name }}
      />
    </Fragment>
  );
}

AvailableItem.displayName = 'AvailableItem';

const mapStateToProps = (state: IAppState, props: OwnProps) => ({
  itemsUsableHere: state.map.itemsUsableHere,
  isChanging: state.outfit.isChanging,
  isEquipped: getIsEquipped(state, props),
});


export default connect(mapStateToProps)(withRouter(AvailableItem));
